/**
 * WasmErrorRecovery - Centralized WASM error handling and recovery system
 *
 * This module provides systematic error recovery for WASM typesetting operations,
 * including client reset, circuit breaker patterns, and graceful degradation.
 */

import { TypesetterWorkerClient } from '../../../wordprocessing/workers/TypesetterWorkerClient';

export interface WasmErrorRecoveryDeps {
  typesetterClientRef: React.RefObject<TypesetterWorkerClient | null>;
  consecutiveWasmErrorsRef: React.RefObject<number>;
  setTypesetterClient: (client: TypesetterWorkerClient | null) => void;
  setIsTypesetterReady: (ready: boolean) => void;
  forceReinitRef: React.RefObject<boolean>;
}

export interface RecoveryResult {
  success: boolean;
  strategy: string;
  shouldRetry: boolean;
}

/**
 * Circuit breaker constants
 */
export const WASM_ERROR_THRESHOLDS = {
  MAX_CONSECUTIVE_ERRORS: 3,
  RESET_TIMEOUT_MS: 5000,
} as const;

/**
 * Error classification for different recovery strategies
 */
export enum WasmErrorType {
  TIMEOUT = 'timeout',
  CORRUPTION = 'corruption',
  NETWORK = 'network',
  UNKNOWN = 'unknown'
}

/**
 * Classifies WASM errors for appropriate recovery strategies
 */
export function classifyWasmError(error: any): WasmErrorType {
  const errorMessage = error?.message || String(error);

  if (errorMessage.includes('timeout') || errorMessage.includes('TimeoutError')) {
    return WasmErrorType.TIMEOUT;
  }

  if (errorMessage.includes('corrupt') || errorMessage.includes('invalid') || errorMessage.includes('memory')) {
    return WasmErrorType.CORRUPTION;
  }

  if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
    return WasmErrorType.NETWORK;
  }

  return WasmErrorType.UNKNOWN;
}

/**
 * Checks if circuit breaker should prevent further WASM operations
 */
export function shouldBreakCircuit(consecutiveErrors: number): boolean {
  return consecutiveErrors >= WASM_ERROR_THRESHOLDS.MAX_CONSECUTIVE_ERRORS;
}

/**
 * Resets WASM client and prepares for recovery
 */
export async function resetWasmClient({
  typesetterClientRef,
  setTypesetterClient,
  setIsTypesetterReady
}: Pick<WasmErrorRecoveryDeps, 'typesetterClientRef' | 'setTypesetterClient' | 'setIsTypesetterReady'>): Promise<void> {
  console.error('🔥 [WasmRecovery] Resetting WASM client due to critical error');

  if (typesetterClientRef.current) {
    try {
      await typesetterClientRef.current.terminate();
      console.log('✅ [WasmRecovery] WASM client terminated successfully');
    } catch (terminateError) {
      console.error('⚠️ [WasmRecovery] Error terminating corrupted WASM client:', terminateError);
    }

    typesetterClientRef.current = null;
    setTypesetterClient(null);
    setIsTypesetterReady(false);
  }
}

/**
 * Executes comprehensive WASM error recovery
 */
export async function executeWasmErrorRecovery(
  error: any,
  deps: WasmErrorRecoveryDeps
): Promise<RecoveryResult> {
  const errorType = classifyWasmError(error);
  const consecutiveErrors = deps.consecutiveWasmErrorsRef.current;

  console.error(`🔥 [WasmRecovery] Executing recovery for ${errorType} error (${consecutiveErrors + 1} consecutive)`);

  // Check circuit breaker
  if (shouldBreakCircuit(consecutiveErrors)) {
    console.error(`🚫 [WasmRecovery] CIRCUIT BREAKER ACTIVATED: ${consecutiveErrors} consecutive errors`);
    return {
      success: false,
      strategy: 'circuit_breaker',
      shouldRetry: false
    };
  }

  try {
    // Strategy 1: Reset WASM client
    await resetWasmClient({
      typesetterClientRef: deps.typesetterClientRef,
      setTypesetterClient: deps.setTypesetterClient,
      setIsTypesetterReady: deps.setIsTypesetterReady
    });

    // Strategy 2: Force reinitialization
    deps.forceReinitRef.current = true;

    // Strategy 3: Increment error counter
    deps.consecutiveWasmErrorsRef.current += 1;

    console.log('✅ [WasmRecovery] Recovery completed successfully');

    return {
      success: true,
      strategy: `reset_client_${errorType}`,
      shouldRetry: true
    };

  } catch (recoveryError) {
    console.error('❌ [WasmRecovery] Recovery failed:', recoveryError);

    return {
      success: false,
      strategy: 'recovery_failed',
      shouldRetry: false
    };
  }
}

/**
 * Resets error counter on successful operations
 */
export function resetErrorCounter(consecutiveWasmErrorsRef: React.RefObject<number>): void {
  if (consecutiveWasmErrorsRef.current > 0) {
    console.log('🔄 [WasmRecovery] Resetting error counter after successful operation');
    consecutiveWasmErrorsRef.current = 0;
  }
}

/**
 * Graceful degradation fallback for when WASM is unavailable
 */
export function createFallbackRenderer(): {
  renderText: (text: string, ctx: CanvasRenderingContext2D) => void;
} {
  return {
    renderText: (text: string, ctx: CanvasRenderingContext2D) => {
      // Simple system font fallback rendering
      ctx.fillStyle = '#666';
      ctx.font = '14px system-ui, -apple-system, sans-serif';
      ctx.fillText(`WASM unavailable - ${text.length} characters`, 10, 30);

      console.warn('⚠️ [WasmRecovery] Using fallback renderer - WASM unavailable');
    }
  };
}
